import { useMemo, useState } from 'react';

import { hasChipMinimizer } from '../ChipMinimizer/ChipMinimizer.utils';
import * as St from './SinkInfoModal.styles';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { SinkInfoModalStep } from '~/core/enums';
import { useMediaQuery } from '~/core/hooks/useMediaQuery';
import { ApplianceModel, ChipMinimizerModel } from '~/core/models';
import ChipMinimizerInfoContent from '~organisms/ChipMinimizerInfoContent';
import SinkInfoContent from '~organisms/SinkInfoContent';

export type AddMinimizerOptions = 'add' | 'remove';

type SinkInfoModalProps = {
  sink: ApplianceModel;
  isVisible: boolean;
  onClose: (value: boolean) => void;
  onSubmit: (chipMinimizer?: ChipMinimizerModel) => void;
};

/**
 * Renders a modal component for displaying sink and chip minimizer information.
 */
const SinkInfoModal = ({
  sink,
  isVisible,
  onClose,
  onSubmit,
}: SinkInfoModalProps) => {
  const { chipMinimizer } = useOptionalItems();
  const [minimizerOption, setMinimizerOption] =
    useState<AddMinimizerOptions>('add');
  const [currentStep, setCurrentStep] = useState<SinkInfoModalStep>(
    SinkInfoModalStep.SINK_INFO
  );

  const isSteppedLayout = useMediaQuery('(max-width: 1080px)');

  const showChipMinimizer = useMemo(
    () => !!chipMinimizer && hasChipMinimizer(sink),
    [chipMinimizer, sink]
  );

  const handleNext = () => {
    if (
      isSteppedLayout &&
      currentStep === SinkInfoModalStep.SINK_INFO &&
      showChipMinimizer
    ) {
      setCurrentStep(SinkInfoModalStep.CHIP_MINIMIZER);
    } else {
      onSubmit(
        showChipMinimizer && minimizerOption === 'add' //the sink shows a chip  minimizer modal because is available for this sink and the user has selected add
          ? chipMinimizer
          : undefined
      );
      setCurrentStep(SinkInfoModalStep.SINK_INFO);
    }
  };

  const handleOnClose = () => {
    setCurrentStep(SinkInfoModalStep.SINK_INFO);
    onClose(false);
  };

  return (
    <St.Container
      type="other"
      title="Sink Info Modal"
      isVisible={isVisible}
      onClose={handleOnClose}
      content={
        <St.Content>
          {(isSteppedLayout
            ? currentStep === SinkInfoModalStep.SINK_INFO
            : true) && (
            <SinkInfoContent
              sink={sink}
              showChipMinimizer={showChipMinimizer && !isSteppedLayout}
            />
          )}
          {showChipMinimizer &&
            (isSteppedLayout
              ? currentStep === SinkInfoModalStep.CHIP_MINIMIZER
              : true) && (
              <ChipMinimizerInfoContent
                chipMinimizer={chipMinimizer}
                setMinimizerOption={setMinimizerOption}
              />
            )}
        </St.Content>
      }
      footer={[
        <St.SelectButton key="select-sink" onClick={handleNext}>
          {isSteppedLayout &&
          currentStep === SinkInfoModalStep.SINK_INFO &&
          showChipMinimizer
            ? 'Next'
            : 'Select'}
        </St.SelectButton>,
      ]}
    />
  );
};

export default SinkInfoModal;
